import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TutorialContext = createContext(null);

export const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    step: 1,
    title: 'Bem-vindo ao Akali AI!',
    description: 'Vamos fazer um tour rápido para você conhecer as principais funcionalidades do sistema.',
    target: null,
    placement: 'center',
    route: '/dashboard',
    action: null
  },
  {
    id: 'create-account',
    step: 2,
    title: 'Crie seu cadastro',
    description: 'Para começar, você criou sua conta informando nome, email profissional, especialidade (Fisioterapeuta ou Instrutor de Pilates) e número de registro no COFFITO (opcional).',
    target: null,
    placement: 'center',
    route: '/dashboard',
    action: null
  },
  {
    id: 'create-patient',
    step: 3,
    title: 'Cadastre seu primeiro paciente',
    description: 'Acesse a seção Pacientes e clique em "Novo Paciente". Preencha os campos mínimos: iniciais ou nome (opcional), idade ou faixa etária, sexo biológico e anotações livres. Não é necessário CPF ou documentos (conformidade LGPD).',
    target: '[data-testid="sidebar-patients"]',
    placement: 'right',
    route: '/dashboard',
    action: 'navigate',
    nextRoute: '/patients'
  },
  {
    id: 'new-patient-button',
    step: 3,
    title: 'Cadastre seu primeiro paciente',
    description: 'Clique no botão "Novo Paciente" para adicionar um paciente ao sistema. Lembre-se: apenas informações essenciais são necessárias.',
    target: '[data-testid="new-patient-btn"]',
    placement: 'bottom',
    route: '/patients',
    action: null
  },
  {
    id: 'select-patient',
    step: 4,
    title: 'Selecione o paciente',
    description: 'Na lista de pacientes, clique sobre o paciente desejado para selecioná-lo antes de iniciar uma sessão de atendimento.',
    target: '[data-testid="patient-item"]',
    placement: 'right',
    route: '/patients',
    action: null
  },
  {
    id: 'start-consultation',
    step: 5,
    title: 'Inicie um novo registro de consulta',
    description: 'Clique em "Nova Consulta" para começar um novo atendimento. O paciente selecionado será automaticamente associado.',
    target: '[data-testid="sidebar-new-consultation"]',
    placement: 'right',
    route: '/patients',
    action: 'navigate',
    nextRoute: '/consultations/new'
  },
  {
    id: 'record-audio',
    step: 6,
    title: 'Grave o áudio da consulta',
    description: 'Use o botão de gravação para registrar a consulta. Fale naturalmente, descrevendo os achados da sessão, avaliação e tratamento realizado. Clique em parar quando terminar.',
    target: '[data-testid="start-recording-btn"]',
    placement: 'bottom',
    route: '/consultations/new',
    action: null
  },
  {
    id: 'generate-document',
    step: 7,
    title: 'Gere o documento com IA',
    description: 'Após a transcrição, clique em "Gerar Documentação Clínica". O sistema irá processar o áudio automaticamente e gerar uma nota clínica estruturada com as seções: Queixa Principal, Histórico, Avaliação, Plano de Tratamento e Evolução.',
    target: '[data-testid="generate-documentation-btn"]',
    placement: 'top',
    route: '/consultations/new',
    action: null
  },
  {
    id: 'review-document',
    step: 8,
    title: 'Revise o documento gerado',
    description: 'Na tela de revisão, você verá a documentação clínica estruturada com todas as seções: Anamnese, Exame Físico, Diagnóstico Funcional, Plano de Tratamento e Orientações ao Paciente. Leia tudo com atenção.',
    target: '[data-testid="consultation-detail-page"]',
    placement: 'center',
    route: null,
    action: null
  },
  {
    id: 'approve-document',
    step: 8,
    title: 'Aprove ou reprove o documento',
    description: 'Revise cada seção e, se estiver de acordo, clique em "Aprovar Documentação". Nenhum documento é salvo sem sua aprovação explícita. Isso é exigido pelas normas do COFFITO para garantir a qualidade e responsabilidade profissional.',
    target: '[data-testid="approve-btn"]',
    placement: 'left',
    route: null,
    action: null
  },
  {
    id: 'complete',
    step: 8,
    title: 'Tutorial concluído!',
    description: 'Agora você está pronto para usar o Akali AI. Se precisar rever este tutorial, acesse Configurações > Replay Tutorial. Bom trabalho!',
    target: null,
    placement: 'center',
    route: null,
    action: 'complete'
  }
];

export function TutorialProvider({ children }) {
  const { user, updateUser } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(true);

  // Check if user should see tutorial on first login
  useEffect(() => {
    if (user && !user.tutorial_completed) {
      setHasSeenTutorial(false);
      // Auto-start tutorial for first-time users after a short delay
      const timer = setTimeout(() => {
        setIsActive(true);
        setCurrentStepIndex(0);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setHasSeenTutorial(true);
    }
  }, [user]);

  const currentStep = TUTORIAL_STEPS[currentStepIndex];
  const totalUniqueSteps = 8; // Total unique steps (not counting sub-steps)

  const startTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < TUTORIAL_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [currentStepIndex]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const skipTutorial = useCallback(async () => {
    setIsActive(false);
    setCurrentStepIndex(0);
    if (user && !user.tutorial_completed) {
      try {
        await updateUser({ tutorial_completed: true });
      } catch (e) {
        console.error('Failed to update tutorial status:', e);
      }
    }
    setHasSeenTutorial(true);
  }, [user, updateUser]);

  const completeTutorial = useCallback(async () => {
    setIsActive(false);
    setCurrentStepIndex(0);
    if (user && !user.tutorial_completed) {
      try {
        await updateUser({ tutorial_completed: true });
      } catch (e) {
        console.error('Failed to update tutorial status:', e);
      }
    }
    setHasSeenTutorial(true);
  }, [user, updateUser]);

  const goToStep = useCallback((index) => {
    if (index >= 0 && index < TUTORIAL_STEPS.length) {
      setCurrentStepIndex(index);
    }
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps: TUTORIAL_STEPS.length,
        totalUniqueSteps,
        hasSeenTutorial,
        startTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        completeTutorial,
        goToStep
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}
